/**
 * The brand surface beside the worker's sign-in.
 *
 * A worker opens this from an SMS on their own phone, so the phone layout
 * comes first: a short gradient band with the mark and the artwork, then the
 * form at full width. A desktop — HR showing the app to somebody, a kiosk at
 * the OHC — gets the panel at full height beside the form instead.
 */

export function BrandPanel() {
  return (
    <aside
      aria-hidden
      className="relative hidden overflow-hidden lg:flex lg:w-[54%] lg:flex-col lg:justify-between
                 bg-gradient-to-br from-brand-navy-deep via-brand-navy to-brand-magenta
                 bg-[length:200%_200%] motion-safe:animate-gradient-drift"
    >
      <Glow />
      <div className="relative px-12 pt-12">
        <img src="/logo.png" alt="" width={240} height={50} className="h-12 w-auto brightness-0 invert" />
      </div>

      <div className="relative flex items-center justify-center px-12">
        <HealthArt className="w-full max-w-[420px]" />
      </div>

      <div className="relative px-12 pb-12">
        <p className="max-w-lg text-[30px] font-bold leading-[1.15] text-white">
          Your checkups, reports and
          <br />
          fitness certificate, in your pocket.
        </p>
        <ul className="mt-6 space-y-2.5 text-[15px] text-white/80">
          <Point>See when your next examination is due, and where.</Point>
          <Point>Read your own reports; your employer sees only the fitness decision.</Point>
          <Point>Show your certificate's QR at the gate — no paper to carry.</Point>
        </ul>
      </div>
    </aside>
  );
}

export function MobileBrandBar() {
  return (
    <div
      aria-hidden
      className="relative overflow-hidden bg-gradient-to-r from-brand-navy-deep via-brand-navy to-brand-magenta px-5 pb-6 pt-5 lg:hidden"
    >
      <Glow />
      <div className="relative flex items-center justify-between gap-4">
        <div>
          <img src="/logo.png" alt="" width={168} height={35} className="h-8 w-auto brightness-0 invert" />
          <p className="mt-3 max-w-[220px] text-[14px] font-semibold leading-snug text-white/90">
            Your checkups, reports and fitness certificate, in your pocket.
          </p>
        </div>
        <HealthArt className="w-[120px] shrink-0" />
      </div>
    </div>
  );
}

function Point({ children }: { children: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-white/80" />
      <span>{children}</span>
    </li>
  );
}

function Glow() {
  return (
    <div
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage:
          'radial-gradient(circle at 18% 22%, rgba(255,255,255,0.16), transparent 42%),'
          + 'radial-gradient(circle at 82% 78%, rgba(255,255,255,0.10), transparent 45%)',
      }}
    />
  );
}

/** A phone showing a pulse and a checklist, with a heart badge over it. */
function HealthArt({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 420 340" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* phone */}
      <rect x="120" y="20" width="180" height="300" rx="28" fill="#fff" fillOpacity="0.12" stroke="#fff" strokeOpacity="0.6" strokeWidth="2.5" />
      <rect x="180" y="34" width="60" height="8" rx="4" fill="#fff" fillOpacity="0.5" />
      {/* screen card: pulse */}
      <rect x="140" y="62" width="140" height="78" rx="12" fill="#fff" fillOpacity="0.9" />
      <path d="M150 104h22l10-22 14 42 12-30 10 14h32" stroke="#E00180" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      {/* checklist rows */}
      {[156, 194, 232].map((y, i) => (
        <g key={y}>
          <rect x="140" y={y} width="140" height="28" rx="8" fill="#fff" fillOpacity={i === 2 ? 0.35 : 0.85} />
          <circle cx="156" cy={y + 14} r="7" fill={i === 2 ? '#fff' : '#159A70'} fillOpacity={i === 2 ? 0.6 : 1} />
          {i !== 2 && <path d={`M152 ${y + 14}l3 3 6-7`} stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />}
          <rect x="172" y={y + 10} width={i === 1 ? 70 : 88} height="8" rx="4" fill="#181868" fillOpacity={i === 2 ? 0.25 : 0.55} />
        </g>
      ))}
      {/* QR tile on the phone */}
      <rect x="140" y="272" width="42" height="36" rx="8" fill="#fff" fillOpacity="0.9" />
      {[[0, 0], [1, 0], [2, 0], [0, 1], [2, 1], [0, 2], [1, 2], [2, 2]].map(([x, y]) => (
        <rect key={`${x}${y}`} x={150 + x * 8} y={280 + y * 7} width="6" height="5" rx="1" fill="#181868" />
      ))}
      <rect x="190" y="280" width="90" height="8" rx="4" fill="#fff" fillOpacity="0.5" />
      <rect x="190" y="296" width="60" height="8" rx="4" fill="#fff" fillOpacity="0.35" />
      {/* heart badge */}
      <circle cx="318" cy="92" r="46" fill="#E00180" stroke="#fff" strokeWidth="3" />
      <path
        d="M318 116c-14-10-30-22-30-36a15 15 0 0 1 30-6 15 15 0 0 1 30 6c0 14-16 26-30 36z"
        fill="#fff"
      />
      {/* floating dots */}
      <circle cx="80" cy="260" r="6" fill="#fff" fillOpacity="0.35" />
      <circle cx="96" cy="70" r="4" fill="#fff" fillOpacity="0.5" />
      <circle cx="340" cy="250" r="5" fill="#fff" fillOpacity="0.3" />
    </svg>
  );
}
